function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import { any, arrayOf, bool, func, object, oneOf, oneOfType, string } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import FormHelperText from '@mui/material/FormHelperText';
import SovosFormControlLabel from '../sovos-form-control-label';
import SovosCheckbox from '../sovos-checkbox';
const Root = styled('div')({});
const StyledLabel = styled(SovosFormControlLabel)(_ref => {
  let {
    size,
    theme: {
      spacing
    }
  } = _ref;
  return {
    // override Mui's default negative margin which expects a large checkbox
    marginInlineStart: size === 'small' && "-".concat(spacing())
  };
});
const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: prop => prop !== 'helperText'
})(_ref2 => {
  let {
    theme: {
      spacing,
      typography
    }
  } = _ref2;
  return {
    ...typography.body2,
    marginBlockStart: spacing(0.5)
  };
});
const SovosCheckboxField = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    checked,
    className,
    classes,
    color = 'primary',
    'data-testid': dataTestId,
    disabled = false,
    error,
    helperText,
    id,
    indeterminate = false,
    label,
    onChange,
    readOnly = false,
    size = 'medium',
    sx,
    value,
    ...props
  } = _ref3;
  const renderCheckbox = () => /*#__PURE__*/React.createElement(SovosCheckbox, _extends({
    checked: checked,
    classes: classes,
    color: color,
    disabled: disabled,
    error: error,
    id: id,
    indeterminate: indeterminate,
    onChange: onChange,
    readOnly: readOnly,
    ref: ref,
    size: size,
    value: value
  }, props));
  return /*#__PURE__*/React.createElement(Root, {
    className: clsx('sovosCheckboxField', className),
    "data-testid": dataTestId,
    sx: sx
  }, /*#__PURE__*/React.createElement(StyledLabel, {
    className: classes === null || classes === void 0 ? void 0 : classes.root,
    control: renderCheckbox(),
    disabled: disabled,
    label: label,
    labelPlacement: "end",
    size: size
  }), helperText ? /*#__PURE__*/React.createElement(StyledHelperText, {
    helperText: helperText,
    error: error
  }, helperText) : null);
});
SovosCheckboxField.propTypes = {
  /**
   * Checkbox label for screen readers
   */
  'aria-label': string,
  /**
   * If `true`, the input is checked
   */
  checked: bool,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Material UI theme color of the component
   */
  color: oneOf(['primary', 'default']),
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * If `true`, the input is disabled
   */
  disabled: bool,
  /**
   * If `true`, the component will display an error state
   */
  error: bool,
  /**
   * Checkbox helper text
   */
  helperText: string,
  /**
   * Id of the checkbox input
   */
  id: string,
  /**
   * If `true`, the input appears indeterminate
   */
  indeterminate: bool,
  /**
   * Checkbox label
   */
  label: string.isRequired,
  /**
   * Name of the input
   */
  name: string,
  /**
   * Callback fired when input value is changed `function(event: object,
   * checked: bool) => void`
   */
  onChange: func,
  /**
   * If `true`, the input will be readonly
   */
  readOnly: bool,
  /**
   * Size of the checkbox
   */
  size: oneOf(['small', 'medium']),
  /**
   * The system prop that allows defining system overrides as well as
   * additional CSS styles.
   */
  sx: oneOfType([arrayOf(oneOfType([func, object, bool])), func, object]),
  /**
   * Value of the input
   */
  value: any
};
export default SovosCheckboxField;